"use client";

import * as React from "react";
import { cn } from "@yardzen/ui/lib/utils";
import { H2 } from "@yardzen/ui/components/h2";
import { P } from "@yardzen/ui/components/p";
import { H3 } from "@yardzen/ui/components/h3";
import { StarRating } from "./StarRating";
import {
  TypeComparisonTable,
  TypeComparisonTableColumn,
  TypeComparisonTableColumnValue,
  TypeStarRating,
} from "@contentful/types";

/* --------------------------------
   Helpers
----------------------------------*/
type ItemsCollection<T> = { itemsCollection?: { items?: T[] | null } };
type ColumnsField<T> = {
  columns?: T[] | null;
  columnsCollection?: { items?: T[] | null } | null;
};

function getMaybeArrayField<T>(fields: ColumnsField<T>): T[] {
  if (Array.isArray(fields.columns) && fields.columns.length)
    return [...fields.columns];
  const coll = fields.columnsCollection?.items;
  return Array.isArray(coll) ? [...coll] : [];
}

function getColumnValues(
  entry: TypeComparisonTableColumn<"WITHOUT_UNRESOLVABLE_LINKS">,
): TypeComparisonTableColumnValue<"WITHOUT_UNRESOLVABLE_LINKS">[] {
  const direct = entry.fields.items as
    | TypeComparisonTableColumnValue<"WITHOUT_UNRESOLVABLE_LINKS">[]
    | undefined;
  if (Array.isArray(direct) && direct.length) return [...direct];

  const coll = (
    entry.fields as unknown as ItemsCollection<
      TypeComparisonTableColumnValue<"WITHOUT_UNRESOLVABLE_LINKS">
    >
  ).itemsCollection?.items;

  return Array.isArray(coll) ? [...coll] : [];
}

/* --------------------------------
   Types
----------------------------------*/
type UIStatus = "yes" | "limited" | "no";
type UIItem = { id: string; title: string; status: UIStatus };
type UIColumn = {
  id: string;
  title: string;
  highlighted?: boolean;
  items: UIItem[];
  starRating?: TypeStarRating<"WITHOUT_UNRESOLVABLE_LINKS">;
};

/* --------------------------------
   Mapping
----------------------------------*/
function mapColumnValue(
  entry: TypeComparisonTableColumnValue<"WITHOUT_UNRESOLVABLE_LINKS">,
): UIItem {
  return {
    id: entry.sys?.id ?? crypto.randomUUID(),
    title: entry.fields.title,
    status: (entry.fields.icon as UIStatus | undefined) ?? "limited",
  };
}

function mapColumn(
  entry: TypeComparisonTableColumn<"WITHOUT_UNRESOLVABLE_LINKS">,
): UIColumn {
  const items = getColumnValues(entry).map(mapColumnValue);
  const starRating = (
    entry.fields as {
      review?: TypeStarRating<"WITHOUT_UNRESOLVABLE_LINKS">;
    }
  ).review;

  return {
    id: entry.sys?.id ?? crypto.randomUUID(),
    title: entry.fields.title,
    highlighted: Boolean(entry.fields.highlighted),
    items,
    starRating,
  };
}
const baseIcon = "h-5 w-5 shrink-0";

const IconYes = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" {...props} className={cn(baseIcon, props.className)}>
    <circle
      cx="10"
      cy="10"
      r="9"
      fill="currentColor"
      className="text-brand-greens-regular"
    />
    <path
      d="M6.5 10.5 9 13 14 8"
      fill="none"
      stroke="white"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconLimited = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" {...props} className={cn(baseIcon, props.className)}>
    <circle
      cx="10"
      cy="10"
      r="9"
      fill="currentColor"
      className="text-brand-primary-regular"
    />
    <line
      x1="6"
      y1="10"
      x2="14"
      y2="10"
      stroke="white"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
);

const IconNo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" {...props} className={cn(baseIcon, props.className)}>
    <circle
      cx="10"
      cy="10"
      r="9"
      fill="currentColor"
      className="text-brand-secondary-regular"
    />
    <path
      d="M7 7 13 13"
      stroke="white"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
    <path
      d="M13 7 7 13"
      stroke="white"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
  </svg>
);

function StatusIcon({ status }: { status: "yes" | "limited" | "no" }) {
  if (status === "yes") return <IconYes />;
  if (status === "limited") return <IconLimited />;
  return <IconNo />;
}

/* --------------------------------
   Card
----------------------------------*/
const Card = ({
  highlighted,
  children,
}: {
  highlighted?: boolean;
  children: React.ReactNode;
}) => (
  <article
    className={cn(
      "bg-brand-beige-light overflow-hidden rounded-2xl border shadow-md",
      highlighted
        ? "ring-brand-greens-light bg-white shadow-xl ring-1"
        : "border-brand-primary-regular/10",
    )}
  >
    {children}
  </article>
);

/* --------------------------------
   Component
----------------------------------*/
export function ComparisonTable({
  comparisonTable,
  className,
}: {
  comparisonTable: TypeComparisonTable<"WITHOUT_UNRESOLVABLE_LINKS">;
  className?: string;
}) {
  const heading = comparisonTable.fields.heading;

  const fields =
    (comparisonTable.fields as unknown as ColumnsField<
      TypeComparisonTableColumn<"WITHOUT_UNRESOLVABLE_LINKS">
    >) ??
    ({} as ColumnsField<
      TypeComparisonTableColumn<"WITHOUT_UNRESOLVABLE_LINKS">
    >);

  const rawColumns =
    getMaybeArrayField<TypeComparisonTableColumn<"WITHOUT_UNRESOLVABLE_LINKS">>(
      fields,
    );

  const columns: UIColumn[] = rawColumns.map(mapColumn);

  return (
    <section className={cn("w-full", className)}>
      {/* Heading */}
      {heading && (
        <div className="mx-auto max-w-5xl px-2 pt-6 text-center md:px-4">
          <H2 className="text-brand-primary-darker text-3xl sm:text-4xl md:text-5xl">
            {heading}
          </H2>
        </div>
      )}

      {/* Fixed 3-column layout */}
      <div className="mx-auto mt-8 grid max-w-6xl grid-cols-3 px-2 md:gap-5 md:px-4">
        {columns.map((col) => (
          <Card key={col.id} highlighted={col.highlighted}>
            <header className="h-[103px] flex-col items-center justify-center px-1 pb-3 pt-4 text-center md:flex md:h-[92px] md:px-5">
              <H3 className="text-md  sm:text-lg md:text-2xl">{col.title}</H3>

              {col.starRating && (
                <div className="mt-2 flex justify-center">
                  <StarRating starRating={col.starRating} variant="center" />
                </div>
              )}
            </header>

            <ol
              className={cn(
                "divide-y",
                col.highlighted
                  ? "divide-brand-beige-light"
                  : "divide-brand-primary-regular/10",
              )}
            >
              {col.items.map((item) => (
                <li key={item.id} className="px-2 py-0 md:px-5">
                  <div className="flex h-[80px] flex-col items-center justify-center gap-1 text-center md:h-[70px]">
                    <StatusIcon status={item.status} />
                    <P noSpacing className="text-[11px] font-medium md:text-xs">
                      {item.title}
                    </P>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        ))}
      </div>
    </section>
  );
}
